/** biome-ignore-all assist/source/useSortedKeys: Order is  */

import { Star } from 'lucide-react';
import { tv, type VariantProps } from 'tailwind-variants';
import { cn } from '@/app/_lib/utils/utils';

const ratingComponent = tv({
  slots: {
    base: 'flex items-start border border-neutral-200 rounded text-neutral-900',
    square: 'bg-neutral-200 flex items-center justify-center font-semibold',
    container: 'flex flex-col bg-white items-center justify-center',
    text: 'font-medium',
    star: '',
    starContainer: 'flex items-center',
  },
  variants: {
    orientation: {
      horizontal: {
        base: '',
        square: 'rounded-l',
      },
      vertical: {
        base: 'flex-col',
        square: 'w-full',
      },
    },
    size: {
      small: {
        container: 'gap-1 px-2 py-1 lg:px-4',
        text: 'text-[10px] lg:text-sm leading-[140%]',
        star: 'w-2 h-2 lg:w-4 lg:h-4',
        starContainer: 'gap-0.5 lg:gap-2',
      },
      large: {
        container: 'gap-2 lg:gap-3.5 px-2 py-1 lg:px-4',
        text: 'text-[10px] lg:text-sm leading-[140%]',
        star: 'w-2 h-2 lg:w-6 lg:h-6',
        starContainer: 'gap-0.5 lg:gap-2',
      },
      stickyBanner: {
        container: 'gap-1 py-1 px-4',
        text: 'text-sm leading-[140%]',
        star: 'w-4 h-4',
        starContainer: 'gap-2',
      },
    },
  },
  compoundVariants: [
    {
      orientation: 'horizontal',
      size: 'small',
      class: {
        square: 'w-9 h-9 text-xs lg:w-12 lg:h-12 lg:text-xl',
      },
    },
    {
      orientation: 'horizontal',
      size: 'stickyBanner',
      class: {
        square: 'w-12 h-12 text-xl',
      },
    },
    {
      orientation: 'horizontal',
      size: 'large',
      class: {
        square: 'w-9 h-9 text-xs lg:w-17 lg:h-17 lg:text-2xl',
      },
    },
    {
      orientation: 'vertical',
      size: 'small',
      class: {
        square: 'h-6 text-sm',
      },
    },
    {
      orientation: 'vertical',
      size: 'large',
      class: {
        square: 'h-8 text-base',
      },
    },
  ],
  defaultVariants: {
    orientation: 'horizontal',
    size: 'small',
  },
});

interface RatingProps extends VariantProps<typeof ratingComponent> {
  rating: number;
  maxRating?: number;
  reviewLabel: string;
  className?: string;
  'data-cy'?: string;
}

export const RatingComponent = ({
  rating,
  maxRating = 5,
  reviewLabel,
  orientation = 'horizontal',
  size = 'small',
  className = '',
  'data-cy': dataCy = 'ndr-rating-component',
}: RatingProps) => {
  const slots = ratingComponent({ orientation, size });

  const renderStars = () => {
    const fullStars = Math.floor(rating);
    const starStep = 0.5;
    const hasHalfStar = rating % 1 >= starStep;

    return Array.from({ length: maxRating }, (_, index) => {
      const starNumber = index + 1;

      if (starNumber <= fullStars) {
        return (
          <Star
            className={cn(slots.star(), 'text-yellow-400 fill-yellow-400')}
            key={`star-full-${starNumber}`}
          />
        );
      }

      if (starNumber === fullStars + 1 && hasHalfStar) {
        return (
          <div
            className='relative'
            key={`star-half-${starNumber}`}
          >
            <Star className={cn(slots.star(), 'text-gray-300')} />
            <div className='absolute inset-0 overflow-hidden w-1/2'>
              <Star className={cn(slots.star(), 'text-yellow-400 fill-yellow-400')} />
            </div>
          </div>
        );
      }

      return (
        <Star
          className={cn(slots.star(), 'text-gray-300')}
          key={`star-empty-${starNumber}`}
        />
      );
    });
  };

  return (
    <div
      className={cn(slots.base(), className)}
      data-cy={dataCy}
    >
      <div
        className={slots.square()}
        data-cy='ndr-rating-value'
      >
        {rating.toFixed(1)}
      </div>

      <div className={slots.container()}>
        <span
          className={slots.text()}
          data-cy='ndr-rating-label'
        >
          {reviewLabel}
        </span>
        <div className={slots.starContainer()}>{renderStars()}</div>
      </div>
    </div>
  );
};
